import { useState, useEffect } from 'react';
import { guestsAPI, propertiesAPI } from '../../lib/api';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Loader2,
  Users,
  Trash2,
  Eye,
  Search,
  Copy,
  Link2,
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminGuests() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewGuest, setViewGuest] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteGuest, setDeleteGuest] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    propertiesAPI.list().then((res) => {
      const props = res.data;
      setProperties(props);
      if (props.length > 0) setSelectedProperty(props[0].id);
    });
  }, []);

  useEffect(() => {
    if (selectedProperty) fetchGuests();
  }, [selectedProperty]);

  const fetchGuests = async () => {
    setLoading(true);
    try {
      const res = await guestsAPI.list(selectedProperty);
      setGuests(res.data.guests || []);
    } catch {
      toast.error('Failed to load guests');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteGuest) return;
    try {
      await guestsAPI.delete(deleteGuest.id);
      toast.success('Guest deleted');
      setDeleteOpen(false);
      fetchGuests();
    } catch {
      toast.error('Failed to delete guest');
    }
  };

  const copyRegistrationLink = () => {
    const prop = properties.find((p) => p.id === selectedProperty);
    if (!prop?.slug) {
      toast.error('Property has no URL slug configured');
      return;
    }
    const url = `${window.location.origin}/register/${prop.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Registration link copied!', { description: url });
  };

  const filtered = guests.filter((g) =>
    !searchQuery ||
    g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    g.phone.includes(searchQuery)
  );

  return (
    <AdminLayout>
      <div className="page-wrapper" data-testid="guests-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="page-title">Guests</h1>
            <p className="text-sm text-muted-foreground mt-1">Registered guests and their details</p>
          </div>
          <Button variant="outline" onClick={copyRegistrationLink} data-testid="copy-reg-link">
            <Link2 className="h-4 w-4 mr-2" />
            Copy Registration Link
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border bg-card">
          <Select value={selectedProperty} onValueChange={setSelectedProperty}>
            <SelectTrigger className="w-[220px]" data-testid="guests-property-select">
              <SelectValue placeholder="Select Property" />
            </SelectTrigger>
            <SelectContent>
              {properties.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search guests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="guests-search"
            />
          </div>
          <Badge variant="secondary">{filtered.length} guest{filtered.length !== 1 ? 's' : ''}</Badge>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No guests registered yet</p>
            <p className="text-sm mt-1">
              Share the registration link with guests to get started.
            </p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Nationality</TableHead>
                  <TableHead>ID/Passport</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((guest) => (
                  <TableRow key={guest.id} data-testid={`guest-row-${guest.id}`}>
                    <TableCell className="font-medium">{guest.name}</TableCell>
                    <TableCell className="text-sm">{guest.email}</TableCell>
                    <TableCell className="text-sm">{guest.phone}</TableCell>
                    <TableCell className="text-sm">{guest.nationality}</TableCell>
                    <TableCell className="text-sm font-mono">{guest.id_passport}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(guest.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setViewGuest(guest); setViewOpen(true); }}
                          data-testid={`view-guest-${guest.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => { setDeleteGuest(guest); setDeleteOpen(true); }}
                          data-testid={`delete-guest-${guest.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* View Guest Detail Dialog */}
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">Guest Details</DialogTitle>
            </DialogHeader>
            {viewGuest && (
              <div className="space-y-3 mt-2 text-sm" data-testid="guest-detail-view">
                <DetailRow label="Name" value={viewGuest.name} />
                <DetailRow label="Email" value={viewGuest.email} />
                <DetailRow label="Phone" value={viewGuest.phone} />
                <DetailRow label="Date of Birth" value={viewGuest.date_of_birth} />
                <DetailRow label="Nationality" value={viewGuest.nationality} />
                <DetailRow label="Country" value={viewGuest.country} />
                <DetailRow label="ID / Passport" value={viewGuest.id_passport} />
                <DetailRow label="Address" value={viewGuest.address} />
                {viewGuest.purpose_of_visit && <DetailRow label="Purpose" value={viewGuest.purpose_of_visit} />}
                {viewGuest.emergency_contact && <DetailRow label="Emergency Contact" value={viewGuest.emergency_contact} />}
                {viewGuest.vehicle_details && <DetailRow label="Vehicle" value={viewGuest.vehicle_details} />}
                {viewGuest.company_name && <DetailRow label="Company" value={viewGuest.company_name} />}
                <DetailRow label="Registered" value={new Date(viewGuest.created_at).toLocaleString()} />
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete confirmation */}
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Guest</AlertDialogTitle>
              <AlertDialogDescription>
                Delete registration for "{deleteGuest?.name}"? This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between border-b border-gray-100 pb-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right max-w-[60%]">{value || '—'}</span>
    </div>
  );
}
